import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ShieldX, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQUIRED_PHRASE = 'DELETE MY ACCOUNT';

/**
 * VendorDangerZone — GDPR "right to erasure" entry point that lives at the
 * bottom of the vendor's Profile tab. Two-step UX so the action can never
 * happen accidentally:
 *
 *   1. Red "Delete my account" button opens a modal explaining exactly what
 *      will happen (anonymise PII, hide products, anonymise enquiry/inbox
 *      references, log out, mobile freed for re-registration).
 *   2. User must type the exact phrase "DELETE MY ACCOUNT" into the confirm
 *      input — Submit stays disabled until the phrase matches verbatim.
 *
 * On success, all vendor localStorage keys are cleared and the user is sent
 * to the homepage. The backend has already cleared the tgm_vendor_jwt cookie.
 */
const VendorDangerZone = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = phrase === REQUIRED_PHRASE && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/vendor/auth/self-delete`,
        { confirm_phrase: phrase, reason },
        { withCredentials: true });
      try {
        localStorage.removeItem('tgmart_vendor');
        localStorage.removeItem('tgmart_vendor_token');
        localStorage.removeItem('tgmart_impersonating');
        window.dispatchEvent(new Event('tgmart-session-change'));
      } catch (_) { /* localStorage unavailable — best-effort */ }
      toast({ title: 'Account deleted', description: 'Your personal data has been removed. Thank you for using TelanganaMart.' });
      navigate('/', { replace: true });
    } catch (err) {
      toast({
        title: 'Could not delete account',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="mt-8 border border-red-200 bg-red-50/40 rounded-lg p-4" data-testid="vendor-danger-zone">
        <div className="flex items-start gap-3">
          <ShieldX className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-red-900">Delete My Account</h3>
            <p className="text-xs text-red-900/80 mt-1">
              Under GDPR you have the right to erase your data. Deleting your account anonymises your business profile, removes your products from public listings, and logs you out permanently. Your mobile number is freed and can be used to register a new account later.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(true)}
              className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
              data-testid="vendor-self-delete-open"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete my account
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={open} onOpenChange={(v) => { if (!submitting) setOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="vendor-self-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Permanently delete account?
            </DialogTitle>
            <DialogDescription>
              This is irreversible. Once submitted:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-xs text-gray-700 space-y-1.5 -mt-2 list-disc pl-5">
            <li>Your name, mobile, email and address are erased from our database.</li>
            <li>All your products are hidden from listings.</li>
            <li>Existing buyer enquiries stay readable for the buyers, but your company name becomes "Deleted Vendor".</li>
            <li>You will be logged out immediately and unable to log back in.</li>
            <li>Your mobile is freed — you can sign up as a new vendor later.</li>
          </ul>

          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="reason" className="text-xs">Reason (optional)</Label>
              <Textarea
                id="reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Help us improve — why are you leaving?"
                data-testid="vendor-self-delete-reason"
              />
            </div>
            <div>
              <Label htmlFor="phrase" className="text-xs">
                Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{REQUIRED_PHRASE}</span> to confirm:
              </Label>
              <Input
                id="phrase"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={REQUIRED_PHRASE}
                autoComplete="off"
                data-testid="vendor-self-delete-phrase"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting} data-testid="vendor-self-delete-cancel">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              data-testid="vendor-self-delete-confirm"
            >
              {submitting ? 'Deleting…' : 'Permanently delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VendorDangerZone;
